import type { Pixels } from '@lemonade-hq/ts-helpers';
import type { ForwardedRef, MutableRefObject } from 'react';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { spacing } from '../../styles/spacing.css';
import { Flex } from '../Flex/Flex';
import { Text } from '../Text/Text';
import * as styles from './Slider.css';

function isContainerRef(ref: ForwardedRef<HTMLDivElement>): ref is MutableRefObject<HTMLDivElement> {
  return ref != null && 'current' in ref && ref.current != null;
}

export interface FloatingBubbleProps {
  readonly label: JSX.Element | string | ((value: number) => JSX.Element);
  readonly min: number;
  readonly max: number;
  readonly value: number[];
}

export function getLabel(value: number, label: FloatingBubbleProps['label']): JSX.Element | string {
  return typeof label === 'function' ? label(value) : label;
}

export const FloatingBubble = forwardRef<HTMLDivElement, FloatingBubbleProps>(({ min, max, value, label }, ref) => {
  const textBubbleRef = useRef<HTMLDivElement>(null);
  const [textPositionLeft, setTextPositionLeft] = useState(0);

  useLayoutEffect(() => {
    /* istanbul ignore if -- @preserve - can't mock null ref */
    if (textBubbleRef.current != null && isContainerRef(ref)) {
      const textBubbleWidth: Pixels = textBubbleRef.current.clientWidth;
      const containerWidth: Pixels = ref.current.clientWidth;

      const progressInPx = (value[0] / (max - min)) * containerWidth;
      setTextPositionLeft(Math.max(textBubbleWidth / 2, Math.min(progressInPx, containerWidth - textBubbleWidth / 2)));
    }
  }, [value, max, min, ref]);

  const currentLabel = getLabel(value[0], label);

  return (
    <Flex
      className={styles.textBubble}
      id="text"
      marginBlockEnd={spacing.s16}
      paddingBlock={spacing.s12}
      paddingInline={spacing.s16}
      position="relative"
      ref={textBubbleRef}
      style={{ left: `${textPositionLeft}px` }}
      width="fit-content"
    >
      <Text type="h6" whiteSpace="nowrap">
        {currentLabel}
      </Text>
    </Flex>
  );
});
